<template>
  <div>
    <!-- Header com filtros e botão novo -->
    <div class="mb-4">
      <!-- Primeira linha: Filtros principais -->
      <div class="flex items-center justify-between mb-3">
        <div class="flex gap-3 flex-1 items-center">
          <div class="view-toggle">
            <button 
              :class="['view-toggle-btn', viewMode === 'cobrancas' ? 'view-toggle-active' : '']"
              @click="setViewMode('cobrancas')"
            >
              Cobranças
            </button>
            <button 
              :class="['view-toggle-btn', viewMode === 'parcelas' ? 'view-toggle-active' : '']"
              @click="setViewMode('parcelas')"
            >
              Parcelas
            </button>
          </div>
          <template v-if="viewMode === 'cobrancas'">
            <select v-model="filtroStatus" @change="carregarCobrancas" class="filtro-select">
              <option value="">{{ t('financeiro.cobrancas.allStatus') }}</option>
              <option value="ATIVO">{{ t('financeiro.cobrancas.statusAtivo') }}</option>
              <option value="PAGO">{{ t('financeiro.cobrancas.statusPago') }}</option>
              <option value="ATRASADO">{{ t('financeiro.cobrancas.statusAtrasado') }}</option>
              <option value="CANCELADO">{{ t('financeiro.cobrancas.statusCancelado') }}</option>
            </select>

            <div class="relative flex-1 max-w-md">
              <input 
                v-model="termoPesquisa" 
                @input="carregarCobrancas"
                type="text" 
                :placeholder="t('financeiro.cobrancas.searchPlaceholder')"
                class="filtro-input w-full"
              />
              <button 
                v-if="termoPesquisa" 
                @click="termoPesquisa = ''; carregarCobrancas()"
                class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✖
              </button>
            </div>
          </template>
        </div>
        <button @click="abrirModalNovaCobranca" class="btn-success ml-3">
          + {{ t('financeiro.cobrancas.newCharge') }}
        </button>
      </div>

      <!-- Segunda linha: Filtro por período de vencimento -->
      <div v-if="viewMode === 'cobrancas'" class="filtro-bar">
        <span class="text-sm font-medium whitespace-nowrap" style="color: var(--text-primary)">📅 {{ t('financeiro.cobrancas.duePeriod') }}</span>
        <div class="flex items-center gap-2 flex-1">
          <button @click="navegarMesCobrancas(-1)" class="month-nav-btn" :title="t('financeiro.cobrancas.prevMonth')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
          </button>
          <input 
            v-model="dataVencimentoInicio" 
            @change="carregarCobrancas"
            type="date" 
            class="filtro-input"
          />
          <span style="color: var(--text-secondary)">{{ t('financeiro.cobrancas.until') }}</span>
          <input 
            v-model="dataVencimentoFim" 
            @change="carregarCobrancas"
            type="date" 
            class="filtro-input"
          />
          <button @click="navegarMesCobrancas(1)" class="month-nav-btn" :title="t('financeiro.cobrancas.nextMonth')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </button>
          <button 
            v-if="dataVencimentoInicio || dataVencimentoFim"
            @click="limparFiltroData"
            class="px-3 py-2 min-w-[110px] rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-all text-sm font-medium"
          >
            ✖ {{ t('financeiro.cobrancas.clearFilter') }}
          </button>
        </div>
      </div>

      <div v-else class="filtro-bar">
        <span class="text-sm font-medium whitespace-nowrap" style="color: var(--text-primary)">📅 {{ t('financeiro.cobrancas.installmentsDue') }}</span>
        <div class="flex items-center gap-2 flex-1">
          <select v-model="filtroStatusParcela" @change="carregarParcelas" class="filtro-select">
            <option value="">{{ t('financeiro.cobrancas.allStatus') }}</option>
            <option value="PENDENTE">{{ t('financeiro.cobrancas.statusPendente') }}</option>
            <option value="PAGO">{{ t('financeiro.cobrancas.statusPago') }}</option>
            <option value="ATRASADO">{{ t('financeiro.cobrancas.statusAtrasado') }}</option>
            <option value="CANCELADO">{{ t('financeiro.cobrancas.statusCancelado') }}</option>
          </select>
          <button @click="navegarMesParcelas(-1)" class="month-nav-btn" :title="t('financeiro.cobrancas.prevMonth')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
          </button>
          <input 
            v-model="dataParcelaInicio" 
            @change="carregarParcelas"
            type="date" 
            class="filtro-input"
          />
          <span style="color: var(--text-secondary)">{{ t('financeiro.cobrancas.until') }}</span>
          <input 
            v-model="dataParcelaFim" 
            @change="carregarParcelas"
            type="date" 
            class="filtro-input"
          />
          <button @click="navegarMesParcelas(1)" class="month-nav-btn" :title="t('financeiro.cobrancas.nextMonth')">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
          </button>
          <button 
            @click="resetParcelasParaMesAtual"
            class="px-3 py-2 min-w-[120px] rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all text-sm font-medium"
          >
            📆 {{ t('financeiro.cobrancas.currentMonth') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Lista de Cobranças -->
    <div v-if="viewMode === 'cobrancas'" class="space-y-4">
      <div v-for="cobranca in cobrancas" :key="cobranca.id" class="cobranca-card">
        <div class="cobranca-header">
          <div class="cobranca-title">
            <div class="cobranca-title-row">
              <h3 class="text-lg font-semibold" style="color: var(--text-primary)">
                📋 {{ cobranca.clienteNome }}
              </h3>
              <span :class="getStatusClass(cobranca.status)" class="px-3 py-1 text-xs rounded-full font-medium">
                {{ cobranca.status }}
              </span>
              <span :class="getTipoClass(cobranca.tipoServico)" class="px-3 py-1 text-xs rounded-full">
                {{ cobranca.tipoServico }}
              </span>
              <span v-if="cobranca.numeroPrestacoes" class="px-3 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                {{ t('financeiro.cobrancas.installmentsCount', { count: cobranca.numeroPrestacoes }) }}
              </span>
            </div>
            <p class="cobranca-desc" style="color: var(--text-secondary)">{{ cobranca.descricao }}</p>
            <div class="cobranca-meta">
              <span v-if="cobranca.dataInicio" class="cobranca-chip">
                📅 {{ t('financeiro.cobrancas.start') }}: {{ formatDate(cobranca.dataInicio) }}
              </span>
              <span v-if="cobranca.dataFim" class="cobranca-chip">
                🏁 {{ t('financeiro.cobrancas.end') }}: {{ formatDate(cobranca.dataFim) }}
              </span>
              <span v-if="cobranca.diaVencimento" class="cobranca-chip">
                💳 {{ t('financeiro.cobrancas.dueEveryDay', { day: cobranca.diaVencimento }) }}
              </span>
            </div>
          </div>
          <div class="flex gap-2">
            <button @click="verPagamentos(cobranca)" 
                    :class="['btn-icon', cobrancaExpandida === cobranca.id ? 'bg-blue-500 text-white' : '']" 
                    :title="cobrancaExpandida === cobranca.id ? t('financeiro.cobrancas.hideInstallments') : t('financeiro.cobrancas.viewInstallments')">
              {{ cobrancaExpandida === cobranca.id ? '🔼' : '💳' }}
            </button>
            <button @click="excluirCobranca(cobranca)" class="btn-icon btn-icon-danger" :title="t('financeiro.cobrancas.deleteCharge')">
              🗑️
            </button>
          </div>
        </div>
        
        <div class="cobranca-stats">
          <div class="cobranca-stat">
            <p class="stat-label">{{ t('financeiro.cobrancas.contract') }}</p>
            <p class="stat-value" style="color: var(--text-primary)">
              {{ cobranca.tipoContrato === 'MENSAL' ? '📅 ' + t('financeiro.cobrancas.contractMonthly') : '💰 ' + t('financeiro.cobrancas.contractInstallments') }}
            </p>
          </div>
          <div class="cobranca-stat">
            <p class="stat-label">{{ t('financeiro.cobrancas.totalValue') }}</p>
            <MoneyAmount :value="cobranca.valorTotal" tag="p" class="stat-value text-green-600" />
          </div>
          <div class="cobranca-stat">
            <p class="stat-label">{{ cobranca.tipoContrato === 'MENSAL' ? t('financeiro.cobrancas.monthlyValue') : t('financeiro.cobrancas.installmentValue') }}</p>
            <MoneyAmount :value="calcularValorParcela(cobranca)" tag="p" class="stat-value text-purple-600" />
          </div>
          <div class="cobranca-stat">
            <p class="stat-label">{{ t('financeiro.cobrancas.paidValue') }}</p>
            <MoneyAmount :value="cobranca.valorPago || 0" tag="p" class="stat-value text-blue-600" />
          </div>
          <div class="cobranca-stat">
            <p class="stat-label">{{ t('financeiro.cobrancas.remaining') }}</p>
            <MoneyAmount :value="cobranca.valorRestante || cobranca.valorTotal" tag="p" class="stat-value text-orange-600" />
          </div>
          <div class="cobranca-stat">
            <p class="stat-label">{{ t('financeiro.cobrancas.progress') }}</p>
            <p class="stat-value" style="color: var(--text-primary)">
              {{ calcularProgresso(cobranca) }}%
            </p>
          </div>
        </div>

        <div class="cobranca-progress">
          <div class="cobranca-progress-track">
            <div class="cobranca-progress-fill" :style="{ width: `${calcularProgresso(cobranca)}%` }"></div>
          </div>
        </div>

        <!-- Parcelas/Pagamentos (expandível) -->
        <div v-if="cobrancaExpandida === cobranca.id" class="mt-4 pt-4 border-t" style="border-color: var(--border)">
          <div class="flex items-center justify-between mb-3">
            <h4 class="font-semibold" style="color: var(--text-primary)">
              💳 {{ t('financeiro.cobrancas.installmentsTitle', { count: pagamentosCobranca.length }) }}
            </h4>
            <span class="text-sm" style="color: var(--text-secondary)">
              {{ t('financeiro.cobrancas.paidOf', { paid: pagamentosCobranca.filter(p => p.status === 'PAGO').length, total: pagamentosCobranca.length }) }}
            </span>
          </div>
          
          <div v-if="pagamentosCobranca.length === 0" class="text-center py-6" style="color: var(--text-secondary)">
            {{ t('financeiro.cobrancas.noInstallmentsYet') }}
          </div>
          
          <div v-else class="space-y-2">
            <div v-for="(pagamento, index) in pagamentosCobranca" :key="pagamento.id" class="cobranca-parcela-card">
              <div class="parcela-header">
                <div class="parcela-left">
                  <span class="parcela-badge" :class="pagamento.status === 'PAGO' ? 'parcela-badge-paid' : 'parcela-badge-pending'">
                    {{ pagamento.numeroPrestacao || (index + 1) }}
                  </span>
                  <div class="parcela-info">
                    <p class="parcela-title" style="color: var(--text-primary)">
                      {{ pagamento.descricao || t('financeiro.cobrancas.installmentDefault', { n: pagamento.numeroPrestacao || (index + 1) }) }}
                    </p>
                    <p class="parcela-subtitle" style="color: var(--text-secondary)">
                      {{ t('financeiro.cobrancas.dueDateLabel') }}: {{ formatDate(pagamento.dataVencimento) }}
                      <span v-if="pagamento.dataPagamento"> • {{ t('financeiro.cobrancas.paidOn') }}: {{ formatDate(pagamento.dataPagamento) }}</span>
                    </p>
                  </div>
                </div>
                <div class="parcela-actions">
                  <MoneyAmount :value="pagamento.valor" class="parcela-valor" style="color: var(--text-primary)" />
                  <span :class="getStatusPagamentoClass(pagamento.status)" class="parcela-status">
                    {{ pagamento.status }}
                  </span>
                  <button 
                          @click="editarParcela(pagamento)" 
                          class="parcela-btn parcela-btn-secondary"
                          :title="t('financeiro.cobrancas.editInstallmentTitle')">
                    ✏️ {{ t('financeiro.cobrancas.editInstallment') }}
                  </button>
                  <button v-if="pagamento.status === 'PENDENTE' && !pagamento.coraInvoiceId" 
                          @click="gerarBoletoManual(pagamento.id)" 
                          class="parcela-btn parcela-btn-primary"
                          :title="t('financeiro.cobrancas.generateBoletoTitle')">
                    📄 {{ t('financeiro.cobrancas.generateBoleto') }}
                  </button>
                  <button v-if="pagamento.status === 'PENDENTE'" 
                          @click="marcarComoPago(pagamento)" 
                          class="parcela-btn parcela-btn-success"
                          :title="t('financeiro.cobrancas.markPaidTitle')">
                    ✅ {{ t('financeiro.cobrancas.markPaid') }}
                  </button>
                </div>
              </div>
              
              <!-- Informações do Boleto Cora -->
              <div v-if="pagamento.coraInvoiceId" class="mt-2 p-2 rounded-lg border-l-4 border-blue-500" style="background-color: var(--bg-secondary)">
                <div class="flex items-center justify-between">
                  <div class="flex-1">
                    <p class="text-xs font-semibold text-blue-600 mb-1">📄 {{ t('financeiro.cobrancas.coraBoleto') }}</p>
                    <p class="text-xs" style="color: var(--text-secondary)">
                      <span class="font-medium">{{ t('financeiro.cobrancas.digitableLine') }}:</span> {{ pagamento.coraDigitableLine || t('financeiro.cobrancas.loading') }}
                    </p>
                  </div>
                  <div class="flex gap-2">
                    <a v-if="pagamento.coraPdfUrl" 
                       :href="pagamento.coraPdfUrl" 
                       target="_blank"
                       class="px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 text-xs font-medium transition-all"
                       :title="t('financeiro.cobrancas.openPdfTitle')">
                      📄 {{ t('financeiro.cobrancas.pdf') }}
                    </a>
                    <button v-if="pagamento.status === 'PENDENTE'"
                            @click="sincronizarPagamento(pagamento.id)" 
                            class="px-2 py-1 rounded bg-purple-600 text-white hover:bg-purple-700 text-xs font-medium transition-all"
                            :title="t('financeiro.cobrancas.syncStatus')">
                      🔄 {{ t('financeiro.cobrancas.sync') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de Parcelas -->
    <div v-else class="space-y-3">
      <div v-if="parcelas.length === 0" class="text-center py-6" style="color: var(--text-secondary)">
        {{ t('financeiro.cobrancas.noInstallmentsPeriod') }}
      </div>
      <div v-else v-for="parcela in parcelas" :key="parcela.id" class="cobranca-parcela-card parcelas-row" @click="onParcelaRowClick(parcela)">
        <div class="parcela-row-grid">
          <div class="parcela-row-section parcela-row-main">
            <button 
              :class="['parcela-badge', parcela.status === 'PAGO' ? 'parcela-badge-paid' : 'parcela-badge-pending', 'parcela-numero']"
              @click.stop="abrirDetalhesCobranca(parcela)"
              :title="`Ver detalhes da cobrança #${parcela.cobrancaId}`"
            >
              {{ parcela.numeroPrestacao || '-' }}
            </button>
            <div class="parcela-info">
              <button 
                class="parcela-title parcela-link"
                @click.stop="abrirDetalhesCliente(parcela)"
                :title="`Ver dados do cliente ${parcela.clienteNome || ''}`"
              >
                {{ parcela.clienteNome || t('financeiro.cobrancas.clientDefault') }}
              </button>
              <div class="parcela-meta" style="color: var(--text-secondary)">
                <span class="parcela-meta-item">{{ t('financeiro.cobrancas.dueDateLabel') }}: {{ formatDate(parcela.dataVencimento) }}</span>
                <span class="parcela-meta-item">{{ t('financeiro.cobrancas.installmentNo', { n: parcela.numeroPrestacao || '-' }) }}</span>
              </div>
            </div>
          </div>
          <div class="parcela-row-section parcela-row-amount">
            <MoneyAmount :value="parcela.valor" class="parcela-valor" style="color: var(--text-primary)" />
            <span :class="getStatusPagamentoClass(parcela.status)" class="parcela-status">
              <span v-if="parcela.status === 'PAGO'">✅</span>
              {{ parcela.status }}
            </span>
          </div>
          <div class="parcela-row-section parcela-row-actions">
            <button 
              v-if="parcela.status === 'PENDENTE'"
              class="parcela-btn parcela-btn-success"
              @click.stop="marcarComoPago(parcela)"
              :title="t('financeiro.cobrancas.markPaidTitle')"
            >
              ✅ {{ t('financeiro.cobrancas.markPaid') }}
            </button>
            <button 
              class="parcela-btn parcela-btn-secondary"
              @click.stop="editarParcela(parcela)"
              :title="t('financeiro.cobrancas.editInstallmentTitle')"
            >
              ✏️ {{ t('financeiro.cobrancas.editInstallment') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Cobrança -->
    <ModalCobranca 
      v-if="modalCobrancaAberto" 
      :cobranca="cobrancaSelecionada"
      @fechar="fecharModalCobranca"
      @salvar="salvarCobranca"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { intlLocaleFor, formatMoneyAmount } from '../../utils/format'
import MoneyAmount from '../MoneyAmount.vue'
import moneyIcon from '../../assets/money-icon.png'
import api from '../../services/api'
import Swal from 'sweetalert2'

const { t, locale } = useI18n()
import ModalCobranca from './ModalCobranca.vue'

const emit = defineEmits(['atualizar'])

const cobrancas = ref([])
const parcelas = ref([])
const viewMode = ref('cobrancas')
const filtroStatus = ref('')
const filtroStatusParcela = ref('')
const termoPesquisa = ref('')
const dataVencimentoInicio = ref('')
const dataVencimentoFim = ref('')
const dataParcelaInicio = ref('')
const dataParcelaFim = ref('')
const modalCobrancaAberto = ref(false)
const cobrancaSelecionada = ref(null)
const cobrancaExpandida = ref(null)
const pagamentosCobranca = ref([])

onMounted(() => {
  carregarCobrancas()
  resetParcelasParaMesAtual(false)
})

function setViewMode(mode) {
  viewMode.value = mode
  if (mode === 'parcelas') {
    resetParcelasParaMesAtual(false)
    carregarParcelas()
  }
}

async function carregarCobrancas() {
  try {
    const params = {}
    if (filtroStatus.value) {
      params.status = filtroStatus.value
    }
    if (termoPesquisa.value && termoPesquisa.value.trim()) {
      params.searchTerm = termoPesquisa.value.trim()
    }
    if (dataVencimentoInicio.value) {
      params.dataVencimentoInicio = dataVencimentoInicio.value
    }
    if (dataVencimentoFim.value) {
      params.dataVencimentoFim = dataVencimentoFim.value
    }
    
    const response = await api.get('/financeiro/cobrancas', { params })
    cobrancas.value = response.data
  } catch (error) {
    console.error('Erro ao carregar cobranças:', error)
    Swal.fire('Erro', t('financeiro.cobrancas.loadChargesError'), 'error')
  }
}

function formatDateInput(date) {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

function normalizeDateInput(value) {
  if (!value) return ''
  if (value instanceof Date) {
    return formatDateInput(value)
  }
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day] = value
    if (year && month && day) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    }
  }
  if (typeof value === 'string') {
    if (value.includes('T')) {
      return value.split('T')[0]
    }
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value
    }
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(value)) {
      return value.replaceAll('/', '-')
    }
    if (/^\d{4},\d{1,2},\d{1,2}$/.test(value)) {
      const [year, month, day] = value.split(',')
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
    }
  }
  return ''
}

function resetParcelasParaMesAtual(recarregar = true) {
  const now = new Date()
  const start = new Date(now.getFullYear(), now.getMonth(), 1)
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  dataParcelaInicio.value = formatDateInput(start)
  dataParcelaFim.value = formatDateInput(end)
  if (recarregar) {
    carregarParcelas()
  }
}

async function carregarParcelas() {
  try {
    if (!dataParcelaInicio.value || !dataParcelaFim.value) {
      resetParcelasParaMesAtual(false)
    }
    const params = {
      dataInicio: dataParcelaInicio.value,
      dataFim: dataParcelaFim.value
    }
    if (filtroStatusParcela.value) {
      params.status = filtroStatusParcela.value
    }
    const response = await api.get('/financeiro/pagamentos', { params })
    parcelas.value = response.data
  } catch (error) {
    console.error('Erro ao carregar parcelas:', error)
    Swal.fire('Erro', t('financeiro.cobrancas.loadInstallmentsError'), 'error')
  }
}

function limparFiltroData() {
  dataVencimentoInicio.value = ''
  dataVencimentoFim.value = ''
  carregarCobrancas()
}

function navegarMesCobrancas(direcao) {
  const ref = dataVencimentoInicio.value ? new Date(dataVencimentoInicio.value + 'T00:00:00') : new Date()
  const novoMes = new Date(ref.getFullYear(), ref.getMonth() + direcao, 1)
  const fimMes = new Date(novoMes.getFullYear(), novoMes.getMonth() + 1, 0)
  dataVencimentoInicio.value = formatDateInput(novoMes)
  dataVencimentoFim.value = formatDateInput(fimMes)
  carregarCobrancas()
}

function navegarMesParcelas(direcao) {
  const ref = dataParcelaInicio.value ? new Date(dataParcelaInicio.value + 'T00:00:00') : new Date()
  const novoMes = new Date(ref.getFullYear(), ref.getMonth() + direcao, 1)
  const fimMes = new Date(novoMes.getFullYear(), novoMes.getMonth() + 1, 0)
  dataParcelaInicio.value = formatDateInput(novoMes)
  dataParcelaFim.value = formatDateInput(fimMes)
  carregarParcelas()
}

async function verPagamentos(cobranca) {
  if (cobrancaExpandida.value === cobranca.id) {
    cobrancaExpandida.value = null
    pagamentosCobranca.value = []
  } else {
    try {
      const response = await api.get('/financeiro/pagamentos', { params: { cobrancaId: cobranca.id } })
      pagamentosCobranca.value = response.data
      cobrancaExpandida.value = cobranca.id
    } catch (error) {
      console.error('Erro ao carregar pagamentos:', error)
      Swal.fire('Erro', t('financeiro.cobrancas.loadPaymentsError'), 'error')
    }
  }
}

const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'zip', 'rar']

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = error => reject(error)
  })
}

async function marcarComoPago(pagamento) {
  let comprovanteFile = null
  const today = new Date().toISOString().split('T')[0]

  const result = await Swal.fire({
    title: t('financeiro.cobrancas.markPaidTitleSwal'),
    html: `
      <div style="display:flex; flex-direction:column; gap:12px; text-align:left;">
        <label style="font-size:12px; color:#6b7280;">' + t('financeiro.cobrancas.paymentDate') + '</label>
        <input type="date" id="dataPagamento" class="swal2-input" value="${today}" style="margin:0;">
        <label style="font-size:12px; color:#6b7280;">' + t('financeiro.cobrancas.receiptLabel') + '</label>
        <div id="comprovanteDrop" style="border:1px dashed #cbd5f5; border-radius:12px; padding:12px; background:#f8fafc; display:flex; flex-direction:column; gap:8px;">
          <div style="display:flex; align-items:center; gap:8px; font-size:13px;">
            <span style="font-size:16px;">📎</span>
            <span>' + t('financeiro.cobrancas.receiptDropHint') + '</span>
          </div>
          <div style="font-size:12px; color:#64748b;">' + t('financeiro.cobrancas.receiptPasteHint') + '</div>
          <input type="file" id="comprovanteFile" accept="image/*,application/pdf" style="display:none;">
          <div id="comprovantePreview" style="font-size:12px; color:#0f172a;"></div>
          <img id="comprovanteImage" style="display:none; width:100%; max-height:160px; object-fit:contain; border-radius:10px; border:1px solid #e2e8f0;">
        </div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: t('common.confirm'),
    cancelButtonText: t('common.cancel'),
    didOpen: () => {
      const drop = document.getElementById('comprovanteDrop')
      const fileInput = document.getElementById('comprovanteFile')
      const preview = document.getElementById('comprovantePreview')
      const previewImage = document.getElementById('comprovanteImage')

      const setPreview = (text) => {
        if (preview) preview.textContent = text || ''
      }

      const handleFile = (file) => {
        if (!file) return
        comprovanteFile = file
        if (file.type && file.type.startsWith('image/')) {
          const objectUrl = URL.createObjectURL(file)
          if (previewImage) {
            previewImage.src = objectUrl
            previewImage.style.display = 'block'
          }
        } else {
          if (previewImage) {
            previewImage.removeAttribute('src')
            previewImage.style.display = 'none'
          }
        }
        setPreview(t('financeiro.cobrancas.fileSelected', { name: file.name }))
      }

      drop?.addEventListener('click', () => fileInput?.click())
      fileInput?.addEventListener('change', (e) => handleFile(e.target.files[0]))
      drop?.addEventListener('dragover', (e) => {
        e.preventDefault()
        drop.style.borderColor = '#3b82f6'
      })
      drop?.addEventListener('dragleave', () => {
        drop.style.borderColor = '#cbd5f5'
      })
      drop?.addEventListener('drop', (e) => {
        e.preventDefault()
        drop.style.borderColor = '#cbd5f5'
        const file = e.dataTransfer?.files?.[0]
        handleFile(file)
      })

      window.addEventListener('paste', (e) => {
        const items = e.clipboardData?.items || []
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            const file = item.getAsFile()
            handleFile(file)
            break
          }
        }
      }, { once: true })
    },
    preConfirm: async () => {
      const dataPagamento = document.getElementById('dataPagamento').value
      const file = comprovanteFile

      if (!file) {
        Swal.showValidationMessage(t('financeiro.cobrancas.attachReceiptRequired'))
        return false
      }

      // Validação semelhante aos anexos (10MB e extensão)
      if (file.size > 10 * 1024 * 1024) {
        Swal.showValidationMessage(t('financeiro.cobrancas.fileTooLarge'))
        return false
      }
      const ext = file.name.split('.').pop()?.toLowerCase() || ''
      if (!allowedExtensions.includes(ext)) {
        Swal.showValidationMessage(t('financeiro.cobrancas.fileTypeNotAllowed', { ext: ext || '?' }))
        return false
      }

      try {
        const base64 = await fileToBase64(file)
        const response = await api.post(`/financeiro/pagamentos/${pagamento.id}/comprovante`, {
          nomeArquivo: file.name,
          nomeOriginal: file.name,
          tipoArquivo: file.type || 'application/octet-stream',
          tamanho: file.size,
          conteudoBase64: base64
        })

        return { dataPagamento, comprovanteUrl: response.data.comprovanteUrl }
      } catch (error) {
        console.error('Erro ao enviar comprovante:', error)
        Swal.showValidationMessage(t('financeiro.cobrancas.uploadReceiptError'))
        return false
      }
    }
  })

  if (result.isConfirmed) {
    try {
      await api.post(`/financeiro/pagamentos/${pagamento.id}/marcar-pago`, null, {
        params: {
          dataPagamento: result.value.dataPagamento,
        comprovanteUrl: result.value.comprovanteUrl
        }
      })
      Swal.fire(t('common.success'), t('financeiro.cobrancas.paymentMarkedPaid'), 'success')
      verPagamentos({ id: pagamento.cobrancaId })
      carregarCobrancas()
      emit('atualizar')
    } catch (error) {
      console.error('Erro ao marcar pagamento:', error)
      Swal.fire('Erro', t('financeiro.cobrancas.markPaymentError'), 'error')
    }
  }
}

async function gerarBoletoManual(pagamentoId) {
  const result = await Swal.fire({
    title: t('financeiro.cobrancas.generateBoletoTitleSwal'),
    text: t('financeiro.cobrancas.generateBoletoText'),
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: t('financeiro.cobrancas.generateBoletoConfirm'),
    cancelButtonText: t('common.cancel'),
    confirmButtonColor: '#2563eb'
  })

  if (result.isConfirmed) {
    try {
      Swal.fire({
        title: t('financeiro.cobrancas.generatingBoleto'),
        text: t('financeiro.cobrancas.generatingBoletoText'),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const response = await api.post(`/financeiro/pagamentos/${pagamentoId}/gerar-boleto`)
      
      Swal.fire({
        title: t('common.success') + '!',
        html: `
          <p class="mb-2">${t('financeiro.cobrancas.boletoGenerated')}</p>
          ${response.data.coraPdfUrl ? `<a href="${response.data.coraPdfUrl}" target="_blank" class="text-blue-600 hover:underline">{{ t('financeiro.cobrancas.openBoletoPdf') }}</a>` : ''}
        `,
        icon: 'success'
      })
      
      // Recarrega a lista de pagamentos da cobrança expandida
      if (cobrancaExpandida.value) {
        const pagsResponse = await api.get('/financeiro/pagamentos', { params: { cobrancaId: cobrancaExpandida.value } })
        pagamentosCobranca.value = pagsResponse.data
      }
      
      carregarCobrancas()
      emit('atualizar')
    } catch (error) {
      console.error('Erro ao gerar boleto:', error)
      Swal.fire('Erro', error.response?.data?.message || t('financeiro.cobrancas.generateBoletoError'), 'error')
    }
  }
}

async function sincronizarPagamento(pagamentoId) {
  try {
    Swal.fire({
      title: 'Sincronizando...',
      text: t('financeiro.cobrancas.syncingBoletoText'),
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    await api.post(`/financeiro/pagamentos/${pagamentoId}/sincronizar-status`)
    
    Swal.fire(t('common.success'), t('financeiro.cobrancas.syncSuccess'), 'success')
    
    // Recarrega a lista de pagamentos da cobrança expandida
    if (cobrancaExpandida.value) {
      const response = await api.get('/financeiro/pagamentos', { params: { cobrancaId: cobrancaExpandida.value } })
      pagamentosCobranca.value = response.data
    }
    
    carregarCobrancas()
    emit('atualizar')
  } catch (error) {
    console.error('Erro ao sincronizar:', error)
    Swal.fire('Erro', error.response?.data?.message || t('financeiro.cobrancas.syncError'), 'error')
  }
}

function isImageUrl(url) {
  return /\.(png|jpg|jpeg|gif|bmp|webp)(\?|#|$)/i.test(url || '')
}

function onParcelaRowClick(parcela) {
  if (parcela.status === 'PENDENTE') {
    marcarComoPago(parcela)
  } else {
    abrirComprovante(parcela)
  }
}

async function abrirComprovante(parcela) {
  const url = parcela?.comprovanteUrl
  if (!url) {
    Swal.fire(t('financeiro.cobrancas.noReceipt'), t('financeiro.cobrancas.noReceiptText'), 'info')
    return
  }
  if (isImageUrl(url)) {
    Swal.fire({
      title: t('financeiro.cobrancas.receiptTitleSwal'),
      imageUrl: url,
      imageAlt: t('financeiro.cobrancas.receiptTitleSwal'),
      showCloseButton: true,
      showConfirmButton: false,
      width: 800
    })
  } else {
    window.open(url, '_blank')
  }
}

async function abrirDetalhesCliente(parcela) {
  try {
    const response = await api.get(`/financeiro/clientes/${parcela.clienteId}`)
    const cliente = response.data
    Swal.fire({
      title: cliente.nome || parcela.clienteNome || t('financeiro.cobrancas.clientDefault'),
      html: `
        <div style="text-align:left; display:flex; flex-direction:column; gap:6px;">
          <div><b>' + t('common.email') + ':</b> ${cliente.email || '-'}</div>
          <div><b>' + t('financeiro.modalCliente.phone') + ':</b> ${cliente.telefone || '-'}</div>
          <div><b>' + t('financeiro.modalCliente.mobile') + ':</b> ${cliente.celular || '-'}</div>
          <div><b>' + t('financeiro.modalCliente.cpfCnpj') + ':</b> ${cliente.cpfCnpj || '-'}</div>
          <div><b>' + t('financeiro.cobrancas.address') + ':</b> ${[cliente.logradouro, cliente.numero, cliente.bairro, cliente.cidade, cliente.estado].filter(Boolean).join(', ') || '-'}</div>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: t('common.close')
    })
  } catch (error) {
    console.error('Erro ao carregar cliente:', error)
    Swal.fire(t('financeiro.cobrancas.clientDetails'), parcela.clienteNome || t('financeiro.cobrancas.clientDefault'), 'info')
  }
}

async function abrirDetalhesCobranca(parcela) {
  try {
    const response = await api.get(`/financeiro/cobrancas/${parcela.cobrancaId}`)
    const cobranca = response.data
    Swal.fire({
      title: t('financeiro.cobrancas.chargeDetails', { id: cobranca.id }),
      html: `
        <div style="text-align:left; display:flex; flex-direction:column; gap:6px;">
          <div><b>' + t('financeiro.modalCobranca.client') + ':</b> ${cobranca.clienteNome || '-'}</div>
          <div><b>' + t('financeiro.modalCobranca.description') + ':</b> ${cobranca.descricao || '-'}</div>
          <div><b>' + t('financeiro.cobrancas.contract') + ':</b> ${cobranca.tipoContrato || '-'}</div>
          <div><b>' + t('common.status') + ':</b> ${cobranca.status || '-'}</div>
          <div><b>' + t('financeiro.cobrancas.totalValue') + ':</b> ${formatMoneyHtml(cobranca.valorTotal || 0)}</div>
          <div><b>' + t('financeiro.cobrancas.paidValue') + ':</b> ${formatMoneyHtml(cobranca.valorPago || 0)}</div>
          <div><b>' + t('financeiro.modalCobranca.startDate') + ':</b> ${formatDate(cobranca.dataInicio)}</div>
          <div><b>' + t('financeiro.cobrancas.end') + ':</b> ${formatDate(cobranca.dataFim)}</div>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: t('common.close')
    })
  } catch (error) {
    console.error('Erro ao carregar cobrança:', error)
    Swal.fire('Erro', t('financeiro.cobrancas.chargeLoadError'), 'error')
  }
}

async function editarParcela(parcela) {
  const valorAtual = parcela?.valor ?? 0
  const vencimentoAtual = normalizeDateInput(parcela?.dataVencimento)

  const result = await Swal.fire({
    title: t('financeiro.cobrancas.editInstallmentTitleSwal'),
    html: `
      <style>
        body.tema_escura .parcela-edit-card {
          border-color: #2a2a2a !important;
          background: linear-gradient(180deg, #1a1a1a 0%, #111111 100%) !important;
          box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4) !important;
        }
        body.tema_escura .parcela-edit-title {
          color: #cbd5f5 !important;
        }
        body.tema_escura .parcela-edit-input {
          background: #0f0f0f !important;
          border-color: #2a2a2a !important;
          color: #f8fafc !important;
        }
        body.tema_escura .parcela-edit-hint {
          color: #94a3b8 !important;
        }
      </style>
      <div style="display:flex; flex-direction:column; gap:16px; text-align:left;">
        <div class="parcela-edit-card" style="display:flex; align-items:center; gap:10px; padding:12px 14px; border:1px solid #e5e7eb; border-radius:14px; background:linear-gradient(180deg,#ffffff 0%,#f8fafc 100%); box-shadow:0 4px 14px rgba(15,23,42,0.06);">
          <div style="width:40px; height:40px; border-radius:12px; background:#e0e7ff; color:#3730a3; display:flex; align-items:center; justify-content:center; font-size:18px;">📅</div>
          <div style="flex:1;">
            <div class="parcela-edit-title" style="font-size:12px; color:#64748b; margin-bottom:6px; font-weight:600; letter-spacing:0.2px;">' + t('financeiro.cobrancas.installmentDueDate') + '</div>
            <input type="date" id="parcelaData" class="swal2-input parcela-edit-input" value="${vencimentoAtual}" style="margin:0; width:100%; border-radius:10px; border:1px solid #e5e7eb; background:#ffffff; padding:10px 12px;">
          </div>
        </div>
        <div class="parcela-edit-card" style="display:flex; align-items:center; gap:10px; padding:12px 14px; border:1px solid #e5e7eb; border-radius:14px; background:linear-gradient(180deg,#ffffff 0%,#f8fafc 100%); box-shadow:0 4px 14px rgba(15,23,42,0.06);">
          <div style="width:40px; height:40px; border-radius:12px; background:#dcfce7; color:#166534; display:flex; align-items:center; justify-content:center; font-size:18px;">💵</div>
          <div style="flex:1;">
            <div class="parcela-edit-title" style="font-size:12px; color:#64748b; margin-bottom:6px; font-weight:600; letter-spacing:0.2px;">' + t('financeiro.cobrancas.installmentValue') + '</div>
            <input type="number" id="parcelaValor" class="swal2-input parcela-edit-input" step="0.01" value="${valorAtual}" style="margin:0; width:100%; border-radius:10px; border:1px solid #e5e7eb; background:#ffffff; padding:10px 12px;">
          </div>
        </div>
        <div class="parcela-edit-hint" style="font-size:12px; color:#64748b; text-align:center;">' + t('financeiro.cobrancas.onlyThisInstallment') + '</div>
      </div>
    `,
    showCancelButton: true,
    confirmButtonText: 'Salvar',
    cancelButtonText: t('common.cancel'),
    preConfirm: () => {
      const dataVencimento = document.getElementById('parcelaData').value
      const valorRaw = document.getElementById('parcelaValor').value
      const valor = Number(String(valorRaw).replace(',', '.'))

      if (!dataVencimento) {
        Swal.showValidationMessage(t('financeiro.cobrancas.dueDateRequired'))
        return false
      }
      if (!valor || valor <= 0) {
        Swal.showValidationMessage(t('financeiro.cobrancas.validValueRequired'))
        return false
      }
      return { dataVencimento, valor }
    }
  })

  if (result.isConfirmed) {
    try {
      await api.put(`/financeiro/pagamentos/${parcela.id}/parcela`, {
        dataVencimento: result.value.dataVencimento,
        valor: result.value.valor
      })
      Swal.fire(t('common.success'), t('financeiro.cobrancas.installmentUpdated'), 'success')
      carregarParcelas()
      if (cobrancaExpandida.value) {
        const response = await api.get('/financeiro/pagamentos', { params: { cobrancaId: cobrancaExpandida.value } })
        pagamentosCobranca.value = response.data
      }
      carregarCobrancas()
    } catch (error) {
      console.error('Erro ao atualizar parcela:', error)
      Swal.fire('Erro', t('financeiro.cobrancas.installmentUpdateError'), 'error')
    }
  }
}

function abrirModalNovaCobranca() {
  cobrancaSelecionada.value = null
  modalCobrancaAberto.value = true
}

function fecharModalCobranca() {
  modalCobrancaAberto.value = false
  cobrancaSelecionada.value = null
}

async function salvarCobranca(cobrancaData) {
  try {
    console.log('📤 Enviando dados da cobrança:', cobrancaData)
    
    if (cobrancaSelecionada.value) {
      await api.put(`/financeiro/cobrancas/${cobrancaSelecionada.value.id}`, cobrancaData)
      Swal.fire(t('common.success'), t('financeiro.cobrancas.chargeUpdated'), 'success')
    } else {
      const response = await api.post('/financeiro/cobrancas', cobrancaData)
      console.log('✅ Resposta:', response.data)
      Swal.fire(t('common.success'), t('financeiro.cobrancas.chargeCreated'), 'success')
    }
    fecharModalCobranca()
    carregarCobrancas()
    emit('atualizar')
  } catch (error) {
    console.error('❌ Erro completo ao salvar cobrança:', error)
    console.error('❌ Response data:', error.response?.data)
    const errorMsg = error.response?.data?.message || error.response?.data?.error || t('financeiro.cobrancas.chargeSaveError')
    Swal.fire('Erro', errorMsg, 'error')
  }
}

async function excluirCobranca(cobranca) {
  const qtdParcelas = cobranca.numeroPrestacoes || 0
  const resultado = await Swal.fire({
    title: t('financeiro.cobrancas.deleteChargeTitle'),
    html: `<p>${t('financeiro.cobrancas.deleteChargeConfirm')} <strong>${cobranca.clienteNome}</strong>?</p>
           <p style="margin-top:8px;color:#dc2626;font-size:14px;">${t('financeiro.cobrancas.deletePaymentsWarning', { count: qtdParcelas })}</p>
           <p style="margin-top:8px;color:#6b7280;font-size:13px;">${t('financeiro.cobrancas.cannotUndo')}</p>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: t('financeiro.cobrancas.deleteYes'),
    cancelButtonText: t('common.cancel')
  })

  if (!resultado.isConfirmed) return

  try {
    await api.delete(`/financeiro/cobrancas/${cobranca.id}`)
    Swal.fire(t('financeiro.cobrancas.deleted'), t('financeiro.cobrancas.chargeDeleted'), 'success')
    carregarCobrancas()
    emit('atualizar')
  } catch (error) {
    console.error('❌ Erro ao excluir cobrança:', error)
    const errorMsg = error.response?.data?.message || error.response?.data?.error || t('financeiro.cobrancas.deleteChargeError')
    Swal.fire('Erro', errorMsg, 'error')
  }
}

function formatMoneyHtml(value) {
  const amount = formatMoneyAmount(value, locale.value)
  return `<span style="display:inline-flex;align-items:center;gap:4px;"><img src="${moneyIcon}" alt="" style="width:14px;height:14px;object-fit:contain" aria-hidden="true" />${amount}</span>`
}

function formatDate(date) {
  if (!date) return '-'
  return new Date(date).toLocaleDateString(intlLocaleFor(locale.value))
}

function getStatusClass(status) {
  const classes = {
    'ATIVO': 'bg-blue-100 text-blue-800',
    'PAGO': 'bg-green-100 text-green-800',
    'ATRASADO': 'bg-red-100 text-red-800',
    'CANCELADO': 'bg-gray-100 text-gray-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

function getTipoClass(tipo) {
  const classes = {
    'MONKEY': 'bg-purple-100 text-purple-800',
    'ONEWAY': 'bg-indigo-100 text-indigo-800',
    'OUTRO': 'bg-yellow-100 text-yellow-800'
  }
  return classes[tipo] || 'bg-gray-100 text-gray-800'
}

function getStatusPagamentoClass(status) {
  const classes = {
    'PENDENTE': 'bg-orange-100 text-orange-800',
    'PAGO': 'bg-green-100 text-green-800',
    'ATRASADO': 'bg-red-100 text-red-800',
    'CANCELADO': 'bg-gray-100 text-gray-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

function calcularProgresso(cobranca) {
  if (!cobranca.valorTotal || cobranca.valorTotal === 0) return 0
  const pago = cobranca.valorPago || 0
  return Math.round((pago / cobranca.valorTotal) * 100)
}

function calcularValorParcela(cobranca) {
  if (cobranca.tipoContrato === 'MENSAL') {
    return cobranca.valorMensal || 0
  } else {
    if (cobranca.numeroPrestacoes && cobranca.numeroPrestacoes > 0) {
      return cobranca.valorTotal / cobranca.numeroPrestacoes
    }
    return cobranca.valorTotal
  }
}
</script>

<style scoped>
.btn-success {
  @apply px-4 py-2 rounded-lg font-medium transition-all bg-green-600 text-white hover:bg-green-700;
}

.btn-icon {
  @apply p-2 rounded-lg transition-all hover:bg-opacity-10 hover:bg-gray-500;
}

.btn-icon-danger {
  @apply hover:bg-red-500 hover:bg-opacity-15;
}

.btn-icon-sm {
  @apply p-1 rounded transition-all hover:bg-opacity-10 hover:bg-green-500;
}

.view-toggle {
  display: inline-flex;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 0.2rem;
  gap: 0.2rem;
}

.view-toggle-btn {
  padding: 0.45rem 0.9rem;
  border-radius: 10px;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-secondary);
  transition: all 0.2s ease;
}

.view-toggle-btn:hover {
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.view-toggle-active {
  background-color: var(--bg-primary);
  color: var(--text-primary);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.filtro-select,
.filtro-input {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.6rem 0.9rem;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.filtro-select:focus,
.filtro-input:focus {
  outline: none;
  border-color: var(--info);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.filtro-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--border);
  background-color: var(--bg-secondary);
}

.month-nav-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid var(--border);
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.month-nav-btn:hover {
  background-color: var(--bg-hover, #f3f4f6);
  color: var(--info, #3b82f6);
  border-color: var(--info, #3b82f6);
  transform: scale(1.05);
}

.month-nav-btn:active {
  transform: scale(0.95);
}

.card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
}

.cobranca-card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.cobranca-card:hover {
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.cobranca-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.cobranca-title {
  flex: 1;
  min-width: 0;
}

.cobranca-title-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
}

.cobranca-desc {
  margin-top: 0.35rem;
  font-size: 0.9rem;
}

.cobranca-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

.cobranca-chip {
  font-size: 0.75rem;
  padding: 0.3rem 0.6rem;
  border-radius: 999px;
  background-color: var(--bg-primary);
  color: var(--text-secondary);
  border: 1px solid var(--border);
}

.cobranca-stats {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;
}

.cobranca-stat {
  background-color: var(--bg-primary);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 0.75rem;
}

.stat-label {
  color: var(--text-secondary);
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-weight: 600;
  font-size: 0.9rem;
}

.cobranca-progress {
  margin-top: 0.9rem;
}

.cobranca-progress-track {
  height: 8px;
  border-radius: 999px;
  background-color: var(--bg-primary);
  border: 1px solid var(--border);
  overflow: hidden;
}

.cobranca-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #22c55e);
  transition: width 0.3s ease;
}

.cobranca-parcela-card {
  background-color: var(--bg-primary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 0.9rem 1rem;
  transition: box-shadow 0.2s ease;
}

.cobranca-parcela-card:hover {
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.1);
}

.parcela-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.parcela-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.parcela-badge {
  width: 36px;
  height: 36px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.85rem;
  border: 1px solid var(--border);
}

.parcela-badge-paid {
  background-color: rgba(16, 185, 129, 0.15);
  color: #16a34a;
}

.parcela-badge-pending {
  background-color: rgba(148, 163, 184, 0.25);
  color: var(--text-primary);
}

.parcela-info {
  min-width: 0;
}

.parcela-row-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 1.25rem;
  align-items: center;
}

.parcela-row-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.parcela-row-main {
  min-width: 0;
}

.parcela-row-amount {
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
}

.parcela-row-actions {
  justify-content: flex-end;
}

.parcela-title {
  font-weight: 600;
  font-size: 0.95rem;
}

.parcela-meta {
  display: flex;
  gap: 0.75rem;
  font-size: 0.78rem;
  margin-top: 0.2rem;
  flex-wrap: wrap;
}

.parcela-meta-item {
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  background-color: var(--bg-primary);
  border: 1px solid var(--border);
}

.parcela-link {
  text-align: left;
  color: var(--text-primary);
}

.parcela-link:hover {
  text-decoration: underline;
}

.parcela-numero {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.parcela-numero:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
}

.parcela-subtitle {
  font-size: 0.8rem;
  margin-top: 0.15rem;
}

.parcela-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.parcela-valor {
  font-weight: 700;
  font-size: 1rem;
}

.parcela-status {
  padding: 0.25rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.parcela-btn {
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
  transition: all 0.2s ease;
}

.parcela-btn-primary {
  background-color: #2563eb;
  color: #fff;
}

.parcela-btn-primary:hover {
  background-color: #1d4ed8;
}

.parcela-btn-success {
  background-color: #16a34a;
  color: #fff;
}

.parcela-btn-success:hover {
  background-color: #15803d;
}

.parcela-btn-secondary {
  background-color: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border);
}

.parcela-btn-secondary:hover {
  background-color: var(--bg-secondary);
}

.parcelas-row {
  cursor: pointer;
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
}

.parcelas-row:hover {
  border-color: rgba(59, 130, 246, 0.4);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

@media (max-width: 1024px) {
  .parcela-row-grid {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
  .parcela-row-amount {
    align-items: flex-start;
    flex-direction: row;
  }
  .parcela-row-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 1280px) {
  .cobranca-stats {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .cobranca-header {
    flex-direction: column;
  }
  .cobranca-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

